import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Field, Input } from '@/components/ui';
import type { QueueTicket } from '@/features/queue/types';
import {
  CANCEL_REASONS,
  cancelReasonText,
  type CancelReason,
} from './cancel-reasons';
import styles from './CancelTicketDialog.module.css';

/**
 * RL-1658: "Cancel ticket" for a waiting patient (a walk-in who left, a
 * ticket made by mistake). One tap for the usual reasons; the card goes at
 * once and an Undo toast stays for 10 seconds before anything is sent.
 */
export function CancelTicketDialog({
  ticket,
  onClose,
  onConfirm,
}: {
  ticket: QueueTicket | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<CancelReason>('left');
  const [note, setNote] = useState('');
  const text = cancelReasonText(reason, note, t);
  const ok = text.trim().length >= 2;
  function submit(e: FormEvent) {
    e.preventDefault();
    if (ok) onConfirm(text);
  }
  return (
    <Dialog
      open={!!ticket}
      onClose={onClose}
      variant="modal"
      title={t('queue.cancel.title', { number: ticket?.number ?? '' })}
      footer={
        <>
          <Button onClick={onClose}>{t('queue.cancel.keep')}</Button>
          <Button
            type="submit"
            form="cancel-ticket-form"
            variant="danger"
            disabled={!ok}
          >
            {t('queue.cancel.confirm')}
          </Button>
        </>
      }
    >
      <form id="cancel-ticket-form" onSubmit={submit}>
        <fieldset className={styles.box}>
          <legend className={styles.legend}>
            {t('queue.cancel.why', { name: ticket?.patient.fullName ?? '' })}
          </legend>
          {CANCEL_REASONS.map((r) => (
            <label key={r} className={styles.option}>
              <input
                type="radio"
                name="cancel-reason"
                value={r}
                checked={reason === r}
                onChange={() => setReason(r)}
              />
              <span>{t(`queue.cancel.reason.${r}`)}</span>
            </label>
          ))}
          {reason === 'other' && (
            <Field label={t('queue.cancel.note')}>
              <Input
                value={note}
                maxLength={300}
                autoFocus
                onChange={(e) => setNote(e.target.value)}
              />
            </Field>
          )}
          <p className={styles.hint}>{t('queue.cancel.hint')}</p>
        </fieldset>
      </form>
    </Dialog>
  );
}
